import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Home, HeartHandshake, Target, Building2 } from "lucide-react";
import { scrollToSection } from "@/components/Header";

const LINES = ["Il tuo", "fisioterapista,", "a casa tua."];
const HERO_IMG = "/matteo.jpg";

const TRUST = [
  { icon: Home, label: "Trattamenti a domicilio" },
  { icon: Target, label: "Obiettivi condivisi e misurabili" },
  { icon: HeartHandshake, label: "Piano di cura su misura" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <h1 className="font-serif tracking-tight text-pine text-4xl sm:text-5xl lg:text-6xl leading-[1.04]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-terracotta" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-8 text-base lg:text-lg text-moss leading-relaxed max-w-xl"
          >
            Niente sale d'attesa, niente spostamenti: porto a casa tua un percorso di recupero
            costruito su misura, con obiettivi chiari che raggiungiamo insieme, passo dopo passo.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            data-testid="hero-studio-note"
            className="mt-8 max-w-xl bg-cream/70 border-l-[3px] border-terracotta rounded-r-md px-6 py-5"
          >
            <p className="font-serif italic text-lg lg:text-xl text-pine leading-snug">
              Non solo a domicilio:{" "}
              <span className="not-italic font-semibold">ricevo anche</span> presso{" "}
              <span className="not-italic font-semibold text-terracotta">Fisioterapia Italia Torino</span>,
              in Via Marenco 28/B.
            </p>
            <p className="mt-3 text-sm text-moss leading-relaxed">
              <span className="font-bold text-pine">Sapevi che?</span> Solo presso il poliambulatorio
              si accettano tutte le principali{" "}
              <button
                data-testid="hero-insurances-link"
                onClick={() => scrollToSection("assicurazioni")}
                className="font-bold text-terracotta underline decoration-terracotta/40 underline-offset-2 hover:decoration-terracotta transition-all duration-300"
              >
                assicurazioni sanitarie
              </button>
              .
            </p>
            <a
              data-testid="hero-studio-map-link"
              href="https://www.google.com/maps/search/?api=1&query=Via+Marenco+28%2FB+Torino"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-2 text-xs font-sans font-bold uppercase tracking-[0.16em] text-terracotta hover:text-pine transition-colors duration-300"
            >
              <Building2 size={14} strokeWidth={1.5} /> Vedi il poliambulatorio sulla mappa
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => scrollToSection("contatti")}
              className="rounded-full bg-terracotta text-bone font-semibold px-8 py-4 hover:bg-pine transition-colors duration-300"
            >
              Prenota una valutazione
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => scrollToSection("trattamenti")}
              className="rounded-full border border-pine/30 text-pine font-semibold px-8 py-4 hover:border-terracotta hover:text-terracotta transition-colors duration-300"
            >
              Scopri i trattamenti
            </button>
          </motion.div>
          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
          >
            {TRUST.map((t) => (
              <li key={t.label} className="flex items-center gap-2 text-sm text-moss">
                <t.icon size={16} strokeWidth={1.5} className="text-terracotta" />
                {t.label}
              </li>
            ))}
          </motion.ul>
        </div>
        <div className="lg:col-span-5 relative">
          <div className="absolute -top-6 -right-6 w-full h-full bg-sage/40 rounded-md" aria-hidden="true" />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.img
              src={HERO_IMG}
              alt="Il Dott. Matteo Caravetta, fisioterapista"
              data-testid="hero-image"
              style={{ y: imgY }}
              className="relative w-full aspect-[4/5] object-cover rounded-md"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
